import { Field } from "@/components/admin/field";
import { FileUpload } from "@/components/admin/file-upload";
import { InterviewForm } from "@/components/admin/interview-form";
import { StageSelect } from "@/components/admin/stage-select";
import { SectionHeader } from "@/components/dashboard/section-header";
import { ErrorState, InlineError } from "@/components/dashboard/state";
import { StatusPill } from "@/components/dashboard/status-pill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAutoSave } from "@/hooks/use-auto-save";
import { type CandidateDetail, adminApi, files } from "@/lib/admin-api";
import { formatFileSize } from "@/lib/utils";
import type { CandidateStatus, InterviewCreate, InterviewUpdate } from "@ow/shared";
import { AUDIT_ACTION_LABELS, DEFAULT_PIPELINE, DOC_TYPE_LABELS, type DocType, stageMeta } from "@ow/shared";

const STATUS_LABELS: Record<CandidateStatus, string> = {
  active: "Active",
  on_hold: "On hold",
  withdrawn: "Withdrawn",
  rejected: "Rejected",
  placed: "Placed",
};

/** Compact one-line summary of an audit diff — the changed field names — so the trail says *what*
 *  moved, not just that something did. The diff shape is provenance JSON, so we read it defensively. */
function diffSummary(diff: unknown): string | null {
  if (diff == null || typeof diff !== "object") return null;
  const keys = Object.keys(diff as Record<string, unknown>);
  return keys.length ? keys.join(", ") : null;
}

// Multi-role surface: each edge belongs to a different role, and this admin view doesn't load each
// role's ladder, so stage labels resolve against the default ladder (custom keys are prettified).
const stageLabel = (key: string) => stageMeta(DEFAULT_PIPELINE, key).label;
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { ArrowLeft, Download, Trash2 } from "lucide-react";
import { useState } from "react";

const STATUSES: CandidateStatus[] = ["active", "on_hold", "withdrawn", "rejected", "placed"];
const numOrNull = (s: string) => (s.trim() === "" ? null : Number.parseFloat(s.replace(/[,$]/g, "")));

export function AdminCandidateEditPage() {
  const { candidateId } = useParams({ strict: false }) as { candidateId: string };
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["admin", "candidate", candidateId],
    queryFn: () => adminApi.candidates.get(candidateId),
  });
  if (isError) return <ErrorState onRetry={() => refetch()}>Couldn't load this candidate.</ErrorState>;
  if (isLoading || !data) return <div className="text-sm text-muted-foreground">Loading…</div>;
  return <Editor key={candidateId} detail={data} />;
}

function Editor({ detail }: { detail: CandidateDetail }) {
  const qc = useQueryClient();
  const id = detail.candidate.id;
  const invalidate = () => qc.invalidateQueries({ queryKey: ["admin", "candidate", id] });
  const { data: searches = [] } = useQuery({
    queryKey: ["admin", "searches"],
    queryFn: () => adminApi.searches.list(),
  });
  const audit = useQuery({ queryKey: ["admin", "audit", id], queryFn: () => adminApi.audit.list("candidate", id) });

  const c = detail.candidate;
  const { values, setField, status } = useAutoSave({
    initial: {
      fullName: c.fullName,
      currentEmployer: c.currentEmployer ?? "",
      currentTitle: c.currentTitle ?? "",
      location: c.location ?? "",
      email: c.email ?? "",
      phone: c.phone ?? "",
      salaryExpectation: c.salaryExpectation?.toString() ?? "",
      noticePeriodDays: c.noticePeriodDays?.toString() ?? "",
      source: c.source ?? "",
      strengths: c.strengths ?? "",
      watchouts: c.watchouts ?? "",
    },
    onSave: async (changed) => {
      const body: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(changed)) {
        if (k === "salaryExpectation") body[k] = numOrNull(String(v));
        else if (k === "noticePeriodDays") body[k] = numOrNull(String(v));
        else body[k] = v === "" ? null : v;
      }
      await adminApi.candidates.update(id, body);
    },
  });

  const [linkSearchId, setLinkSearchId] = useState("");
  const addLink = useMutation({
    mutationFn: () => adminApi.links.create(id, { searchId: linkSearchId, stage: "longlist", status: "active" }),
    onSuccess: () => {
      invalidate();
      setLinkSearchId("");
    },
  });
  const moveStage = useMutation({
    mutationFn: (v: { linkId: string; stage?: string; status?: CandidateStatus }) =>
      adminApi.links.moveStage(v.linkId, { stage: v.stage, status: v.status }),
    onSuccess: invalidate,
  });
  const removeLink = useMutation({
    mutationFn: (linkId: string) => adminApi.links.remove(linkId),
    onSuccess: invalidate,
  });
  const addInterview = useMutation({
    mutationFn: (v: { linkId: string; body: InterviewCreate }) => adminApi.interviews.create(v.linkId, v.body),
    onSuccess: invalidate,
  });
  const delInterview = useMutation({
    mutationFn: (iid: string) => adminApi.interviews.remove(iid),
    onSuccess: invalidate,
  });
  const delDoc = useMutation({ mutationFn: (did: string) => files.remove(did), onSuccess: invalidate });
  const updateInterview = useMutation({
    mutationFn: (v: { id: string; body: InterviewUpdate }) => adminApi.interviews.update(v.id, v.body),
    onSuccess: () => {
      invalidate();
      setEditingIv(null);
    },
  });

  const [interviewEdge, setInterviewEdge] = useState("");
  const [editingIv, setEditingIv] = useState<CandidateDetail["interviews"][number] | null>(null);
  // After a stage move, offer to log the round the candidate just completed.
  const [pendingLog, setPendingLog] = useState<{ linkId: string; round: string; searchTitle: string } | null>(null);
  const [logRound, setLogRound] = useState<string | undefined>(undefined);
  const text = (k: keyof typeof values) => ({
    value: values[k],
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setField(k, e.target.value),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Link
          to="/admin/candidates"
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-4" /> Candidates
        </Link>
        <span className="eyebrow">
          {status === "saving" ? "Saving…" : status === "saved" ? "Saved" : status === "error" ? "Save failed" : ""}
        </span>
      </div>

      <SectionHeader title={c.fullName} />

      <InlineError
        error={
          addLink.error ??
          moveStage.error ??
          removeLink.error ??
          addInterview.error ??
          updateInterview.error ??
          delInterview.error ??
          delDoc.error
        }
      />

      {/* Core fields — auto-saved */}
      <section className="grid grid-cols-2 gap-3 rounded-xl border bg-card p-4 sm:grid-cols-3">
        <Field label="Full name">
          <Input {...text("fullName")} />
        </Field>
        <Field label="Current employer">
          <Input {...text("currentEmployer")} />
        </Field>
        <Field label="Current title">
          <Input {...text("currentTitle")} />
        </Field>
        <Field label="Location">
          <Input {...text("location")} />
        </Field>
        <Field label="Email">
          <Input {...text("email")} />
        </Field>
        <Field label="Phone">
          <Input {...text("phone")} />
        </Field>
        <Field label="Salary expectation">
          <Input {...text("salaryExpectation")} inputMode="decimal" />
        </Field>
        <Field label="Notice (days)">
          <Input {...text("noticePeriodDays")} inputMode="numeric" />
        </Field>
        <Field label="Source">
          <Input {...text("source")} />
        </Field>
        <Field label="Strengths" className="col-span-full">
          <Textarea {...text("strengths")} />
        </Field>
        <Field label="Watchouts / development areas" className="col-span-full">
          <Textarea {...text("watchouts")} />
        </Field>
      </section>

      {/* Search links */}
      <section className="space-y-2">
        <SectionHeader title="Searches" />
        <div className="space-y-2">
          {detail.edges.map((edge) => (
            <div key={edge.id} className="flex flex-wrap items-center gap-3 rounded-lg border bg-card p-3">
              <span className="font-medium">{edge.searchTitle}</span>
              <StageSelect
                value={edge.stage}
                onChange={(stage) => {
                  const previous = edge.stage;
                  moveStage.mutate(
                    { linkId: edge.id, stage },
                    {
                      onSuccess: () => {
                        if (previous !== stage) {
                          setPendingLog({
                            linkId: edge.id,
                            round: stageLabel(previous),
                            searchTitle: edge.searchTitle,
                          });
                        }
                      },
                    },
                  );
                }}
              />
              <select
                value={edge.status}
                onChange={(e) => moveStage.mutate({ linkId: edge.id, status: e.target.value as CandidateStatus })}
                className="h-9 rounded-md border border-input bg-transparent px-2 text-sm"
              >
                {STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {STATUS_LABELS[s]}
                  </option>
                ))}
              </select>
              <Button size="xs" variant="ghost" className="ml-auto" onClick={() => removeLink.mutate(edge.id)}>
                Unlink
              </Button>
            </div>
          ))}
          <div className="flex items-center gap-2">
            <select
              value={linkSearchId}
              onChange={(e) => setLinkSearchId(e.target.value)}
              className="h-9 rounded-md border border-input bg-transparent px-2 text-sm"
            >
              <option value="">Link to a search…</option>
              {searches
                .filter((s) => !detail.edges.some((e) => e.searchId === s.id))
                .map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.title}
                  </option>
                ))}
            </select>
            <Button
              size="sm"
              variant="outline"
              disabled={!linkSearchId}
              title={!linkSearchId ? "Choose a search from the list to link this candidate" : undefined}
              onClick={() => addLink.mutate()}
            >
              Link
            </Button>
          </div>
        </div>
      </section>

      {/* Documents */}
      <section className="space-y-2">
        <SectionHeader title="Documents">
          <FileUpload candidateId={id} onUploaded={invalidate} />
        </SectionHeader>
        <div className="rounded-xl border bg-card divide-y">
          {detail.documents.length === 0 ? (
            <div className="p-3 text-sm text-muted-foreground">No documents uploaded.</div>
          ) : (
            detail.documents.map((d) => (
              <div key={d.id} className="flex items-center gap-3 p-3 text-sm">
                <span className="font-mono text-xs uppercase text-muted-foreground">
                  {DOC_TYPE_LABELS[d.docType as DocType] ?? d.docType} · v{d.version}
                </span>
                <span className="font-medium">{d.fileName}</span>
                <span className="text-xs text-muted-foreground">{formatFileSize(d.byteSize)}</span>
                <a href={files.downloadUrl(d.id)} target="_blank" rel="noreferrer" className="ml-auto">
                  <Button size="xs" variant="ghost">
                    <Download className="size-3.5" />
                  </Button>
                </a>
                <Button size="xs" variant="ghost" onClick={() => delDoc.mutate(d.id)}>
                  <Trash2 className="size-3.5" />
                </Button>
              </div>
            ))
          )}
        </div>
      </section>

      {/* Interview log */}
      <section className="space-y-2">
        <SectionHeader title="Interview log" />
        {detail.edges.length === 0 ? (
          <p className="text-sm text-muted-foreground">Link the candidate to a search before logging interviews.</p>
        ) : (
          <>
            {pendingLog ? (
              <div className="flex flex-wrap items-center gap-2 rounded-md border border-[var(--brand-accent)] bg-accent/40 p-2 text-sm">
                <span>
                  Stage moved — log the <strong>{pendingLog.round}</strong> outcome for {pendingLog.searchTitle}?
                </span>
                <Button
                  size="xs"
                  className="ml-auto"
                  onClick={() => {
                    setInterviewEdge(pendingLog.linkId);
                    setLogRound(pendingLog.round);
                    setEditingIv(null);
                    setPendingLog(null);
                  }}
                >
                  Log it
                </Button>
                <Button size="xs" variant="ghost" onClick={() => setPendingLog(null)}>
                  Dismiss
                </Button>
              </div>
            ) : null}
            <div className="flex items-center gap-2">
              <select
                value={interviewEdge}
                onChange={(e) => {
                  setInterviewEdge(e.target.value);
                  setLogRound(undefined);
                }}
                className="h-9 rounded-md border border-input bg-transparent px-2 text-sm"
              >
                <option value="">Add interview to…</option>
                {detail.edges.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.searchTitle}
                  </option>
                ))}
              </select>
            </div>
            {interviewEdge ? (
              <InterviewForm
                key={`${interviewEdge}-${logRound ?? ""}`}
                onSubmit={(body) =>
                  addInterview.mutate({ linkId: interviewEdge, body }, { onSuccess: () => setLogRound(undefined) })
                }
                submitting={addInterview.isPending}
                defaultRound={
                  logRound ?? stageLabel(detail.edges.find((e) => e.id === interviewEdge)?.stage ?? "longlist")
                }
                onCancel={() => {
                  setInterviewEdge("");
                  setLogRound(undefined);
                }}
              />
            ) : null}
            <div className="space-y-2">
              {detail.interviews.map((iv) =>
                editingIv?.id === iv.id ? (
                  <InterviewForm
                    key={iv.id}
                    initial={iv}
                    submitting={updateInterview.isPending}
                    onSubmit={(body) => updateInterview.mutate({ id: iv.id, body })}
                    onCancel={() => setEditingIv(null)}
                  />
                ) : (
                  <div key={iv.id} className="rounded-lg border bg-card p-3 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{iv.roundName}</span>
                      <span className="text-xs text-muted-foreground">· {iv.searchTitle}</span>
                      {iv.recommendation ? <StatusPill value={iv.recommendation} /> : null}
                      {iv.isPrivate ? (
                        <span className="font-mono text-[11px] uppercase text-[var(--color-warning)]">private</span>
                      ) : null}
                      <div className="ml-auto flex items-center gap-1">
                        <Button
                          size="xs"
                          variant="ghost"
                          onClick={() => {
                            setEditingIv(iv);
                            setInterviewEdge("");
                          }}
                        >
                          Edit
                        </Button>
                        <Button size="xs" variant="ghost" onClick={() => delInterview.mutate(iv.id)}>
                          <Trash2 className="size-3.5" />
                        </Button>
                      </div>
                    </div>
                    {iv.feedbackNotes ? <p className="mt-1 text-muted-foreground">{iv.feedbackNotes}</p> : null}
                  </div>
                ),
              )}
            </div>
          </>
        )}
      </section>

      {/* Audit trail */}
      <section className="space-y-2">
        <SectionHeader title="Audit trail" />
        <div className="rounded-xl border bg-card divide-y text-sm">
          {(audit.data ?? []).slice(0, 10).map((a) => {
            const changed = diffSummary(a.diff);
            return (
              <div key={a.id} className="flex items-center gap-2 p-2 text-xs">
                <span className="font-medium text-foreground">{AUDIT_ACTION_LABELS[a.action]}</span>
                <span className="text-muted-foreground">{a.entityType}</span>
                {changed ? <span className="truncate text-muted-foreground">· {changed}</span> : null}
                <span className="ml-auto shrink-0 text-muted-foreground">by {a.changedBy ?? "system"}</span>
                <span className="shrink-0 text-muted-foreground/70">{new Date(a.createdAt).toLocaleString()}</span>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
